import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { CommonModule, DecimalPipe } from "@angular/common";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { SharedModule } from "../../../../shared/shared.module";
import { DialogHeaderExportComponent } from "../../../../shared/modules/dialog/dialog-header-export/dialog-header-export.component";
import { InputSearchComponent } from "../../../../shared/modules/input-search/input-search.component";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { RangePipe } from "../../../../shared/pipes/range.pipe";
import { exportProductiviteExcel } from "./productivite-excel.utils";

interface Row {
  ligneDenomination: string;
  score: string;
  score_poste: string;
}

export interface ProductiviteDialogData {
  rows: Row[];
  /** OFs source pour les analyses détaillées + données brutes Excel. */
  ofs?: import("../../../of/models/of.model").OF[];
}

type SortKey = "ligne" | "score" | "score_poste";

@Component({
  selector: "vl-productivite-dialog",
  standalone: true,
  imports: [
    CommonModule,
    DecimalPipe,
    SharedModule,
    DialogHeaderExportComponent,
    InputSearchComponent,
    RadioCardComponent,
    RangePipe,
    MatIcon,
    MatTooltip,
  ],
  templateUrl: "./productivite-dialog.component.html",
  styleUrl: "./productivite-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductiviteDialogComponent {
  data = inject<ProductiviteDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<ProductiviteDialogComponent>);

  exporting = signal(false);
  search = signal<string>("");
  sortKey = signal<SortKey>("score");
  sortDesc = signal<boolean>(true);

  filtered = computed<Row[]>(() => {
    const all = this.data.rows ?? [];
    const q = this.search().trim().toLowerCase();
    let list = all;
    if (q) {
      list = list.filter((r) =>
        r.ligneDenomination.toLowerCase().includes(q),
      );
    }
    return this.sortList(list);
  });

  stats = computed(() => {
    const rs = this.data.rows ?? [];
    const total = rs.length;
    if (total === 0) {
      return {
        total: 0,
        avgScore: 0,
        topLabel: "—",
        topScore: 0,
        worstLabel: "—",
        worstScore: 0,
        lowPerf: 0,
      };
    }
    const sumScore = rs.reduce((s, r) => s + Number(r.score), 0);
    const avgScore = sumScore / total;

    const sorted = [...rs].sort((a, b) => Number(b.score) - Number(a.score));
    const top = sorted[0];
    const worst = sorted[sorted.length - 1];

    // Lignes "sous performance" = score_poste <= 2
    const lowPerf = rs.filter((r) => Number(r.score_poste) <= 2).length;

    return {
      total,
      avgScore,
      topLabel: top.ligneDenomination,
      topScore: Number(top.score),
      worstLabel: worst.ligneDenomination,
      worstScore: Number(worst.score),
      lowPerf,
    };
  });

  toggleSort(key: SortKey): void {
    if (this.sortKey() === key) {
      this.sortDesc.set(!this.sortDesc());
    } else {
      this.sortKey.set(key);
      this.sortDesc.set(key !== "ligne");
    }
  }

  private sortList(list: Row[]): Row[] {
    const key = this.sortKey();
    const desc = this.sortDesc();
    const mult = desc ? -1 : 1;
    return [...list].sort((a, b) => {
      switch (key) {
        case "ligne":
          return a.ligneDenomination.localeCompare(b.ligneDenomination) * mult;
        case "score":
          return (Number(a.score) - Number(b.score)) * mult;
        case "score_poste":
          return (Number(a.score_poste) - Number(b.score_poste)) * mult;
      }
    });
  }

  async exportExcel(): Promise<void> {
    this.exporting.set(true);
    try {
      await exportProductiviteExcel({
        rows: this.filtered(),
        ofs: this.data.ofs ?? [],
        stats: this.stats(),
      });
    } finally {
      this.exporting.set(false);
    }
  }
}
